import { Component, ElementRef, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from "src/app/shared/auth/services/auth.service";
import { AtribuicaoLoginSed, UserLoginSed } from "src/app/shared/models/sed/UserLogin";
import { FiltroTabulator } from 'src/app/shared/models/tabulator/FiltroTabulator';
import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';
import { Options, RowComponent, TabulatorFull as Tabulator } from 'tabulator-tables';
import { DateTime } from "luxon"
import * as XLSX from 'xlsx';
import { HttpClient } from '@angular/common/http';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { environment } from 'src/environments/environment';

type ExportType = 'auto' | 'string' | 'number' | 'boolean' | 'date' | 'time';

@Component({
  selector: 'app-tabulator-table',
  templateUrl: './tabulator-table.component.html',
  styleUrls: ['./tabulator-table.component.scss'],
  standalone: true,
  imports: [angularCommonImports, libsCommonImports]
})
export class TabulatorTableComponent implements OnChanges, OnInit {
  logoBase64: string | undefined;
  periodoEleitoral: boolean = environment.periodoEleitoral;

  @ViewChild('tableHolder', { static: true })
  private tableHolder!: ElementRef<HTMLDivElement>;

  public router = inject(Router);
  public spinner = inject(NgxSpinnerService);
  public http = inject(HttpClient);

  @Input() tableData: any[] = [];
  @Input() columnNames: any[] = [];
  @Input() height: string = '580px';
  @Input() options?: Options;
  @Input() tableTitle: string = "Tabela Conviva";
  @Input() orientacaoPortrait: boolean = true;
  @Input() tipoRelatorio: number = 0;
  @Input() filtros: FiltroTabulator[] = []
  @Input() mostrarExportarExcel: boolean = true
  @Input() mostrarExportarPDF: boolean = true
  @Input() mostrarImprimir: boolean = true
  @Input() mostrarExportarHtml: boolean = true
  @Input() mostrarFiltro: boolean = true
  @Input() colunasNaoExportaveis: string[] = []
  @Input() rowFormatter: ((row: RowComponent) => any);
  @Input() subTable: boolean = false
  @Input() subColumnNames: (ctx: any, pai: any) => any[];
  @Input() legendaHtml?: string;
  /** #155893 — Painel Psicólogo: força redraw da coluna Ações após filtro de Escola. */
  @Input() refreshKey = 0;




  /*
    0 = Painel Geral
    1 = Painel de Avaliação
    2 = Agendamento Psicologos
  */
  // list properties you want to set per implementation here...

  tab = document.createElement('div');

  private table: Tabulator;
  public userLogado: UserLoginSed | null;
  public atribuicoesLogin: AtribuicaoLoginSed;
  private programmaticPageChange: boolean = false;

  constructor(private authService: AuthService) {
    // Dados usuário logado
    this.userLogado = this.authService.getUser();
    if (this.userLogado != null && !this.userLogado.flPsicologo) {
      this.atribuicoesLogin = this.userLogado?.DadosUsuario[0];
    }

    (window as any).DateTime = DateTime
  }

  public formFilter!: FormGroup;
  public get form() { return this.formFilter.controls; }

  ngOnInit(): void {
    this.formFilter = new FormGroup({
      filterField: new FormControl(null),
      filterValue: new FormControl(null),
      filterType: new FormControl(null)
    });

    this.loadLogo();
  }

  ngOnChanges(ch: SimpleChanges) {
    if (!this.table) return; // antes do init
    if (ch['tableData'] && !ch['tableData'].firstChange) {
      const currentPage = this.table.getPage();
      this.table.setData(this.tableData);       // só injeta dataset novo
      setTimeout(() => {
        const maxPages = this.table.getPageMax();
        const pageToSet = this.userLogado?.currentPage || currentPage;
        if (pageToSet && pageToSet <= (maxPages as number)) {
          this.programmaticPageChange = true;
          this.table.setPage(pageToSet);
        }
      }, 100);
    }
    if (ch['columnNames'] && !ch['columnNames'].firstChange) {
      this.table.setColumns(this.columnNames);  // só troca colunas
    }
    if (ch['refreshKey'] && !ch['refreshKey'].firstChange) {
      // #155893 — Redesenha formatters da coluna Ações após jornada ativa carregada com filtro de Escola.
      this.table.redraw(true);
    }
  }

  ngAfterViewInit() {
    this.setOptions();
    this.buildTable();       // 1 única vez
  }

  public clearFilters() {
    this.formFilter.get('filterField')?.patchValue(null);
    this.formFilter.get('filterType')?.patchValue(null);
    this.formFilter.get('filterValue')?.patchValue(null);
  }

  public filtarValue() {
    let field = this.formFilter.get('filterField')?.value;
    let value = this.formFilter.get('filterValue')?.value;
    let type = this.formFilter.get('filterType')?.value;

    if (field && value && type) {
      this.table.setFilter(field, type, value);
    }
  }

  private setOptions(): void {

    this.options = {
      data: this.tableData,
      reactiveData: true, //enable data reactivity
      columns: this.columnNames,
      pagination: true, //enable.
      paginationSize: 10,
      paginationSizeSelector: [10, 25, 50],
      paginationCounter: "rows",
      placeholder: "Nenhum dado encontrado",
      height: this.height,
      layout: "fitColumns",
      renderHorizontal: "virtual",
      locale: true,
      langs: {
        "pt-br": {
          "data": {
            "loading": "Carregando",
            "error": "Erro",
          },
          "groups": {
            "item": "item",
            "items": "itens",
          },
          "pagination": {
            "page_size": "Tamanho da Página",
            "page_title": "Mostrar Pagina",
            "first": "Primeiro",
            "first_title": "Primeira Página",
            "last": "Último",
            "last_title": "Última Pagina",
            "prev": "Anterior",
            "prev_title": "Pagina Anterior",
            "next": "Próximo",
            "next_title": "Próxima Pagina",
            "all": "Tudo",
            "counter": {
              "showing": "Visualizando",
              "of": "de",
              "rows": "linhas",
              "pages": "paginas",
            }
          },
        }
      },
      rowFormatter: this.rowFormatter,
      rowFormatterPrint: (row) => {
        return row.getData();
      },
      printHeader: `
        <div style="
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 16px;
          margin-bottom: 12px;
          overflow: hidden;
        ">
          <div style="
            flex: 1;
            text-align: center;
            font-size: 20px;
            font-weight: bold;
            font-family: Arial, sans-serif;
            color: #003366;
          ">
            ${this.tableTitle}
          </div>
          <div style="flex-shrink:0;">
            <img src="${this.periodoEleitoral ? '//sedseecdn.azureedge.net/images/favicon_eleitoral/ms-icon-70x70.png' : './assets/img/logo-gov-sp.png'}"
                 alt="Logo do Governo de São Paulo"
                 style="
                  width: ${this.periodoEleitoral ? '50px' : '230px'};
                  height: ${this.periodoEleitoral ? '50px' : '70px'};
                  display:block;
                  border: ${this.periodoEleitoral ? '0' : '2px solid #000'};
                  border-radius: 0 30px 30px 0;
            ">
          </div>
        </div>
      `,
      printFooter: `<div class="mt-4">Usuário logado: ${this.userLogado?.nome}</div>`
    };
  }



  public ExportarComoPDF() {

    this.spinner.show();

    const tableTitle = this.tableTitle;

    for (const col of this.colunasNaoExportaveis) {
      this.table.deleteColumn(col);
    }

    this.table.download("pdf", tableTitle + ".pdf", {
      orientation: this.orientacaoPortrait ? "portrait" : "landscape",
      autoTable: (doc) => {

        const truncate = (v: any, max = 40) =>
          typeof v === "string" && v.length > max ? v.slice(0, max) + "…" : v;

        return {
          // começa a tabela mais abaixo para não sobrepor cabeçalho
          startY: 55,
          showHead: 'everyPage',
          margin: { top: 55, left: 12, right: 12, bottom: 12 },
          headStyles: {
            fillColor: [0, 123, 167],
            textColor: 255,
            fontStyle: "bold",
            fontSize: 8,
            halign: "center",
          },
          styles: {
            fontSize: 7,
            cellPadding: 2, // ✅ no lugar do padding
            overflow: "linebreak",
            valign: "middle",
            lineColor: 220,
            lineWidth: 0.1,
          },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          didParseCell: (hook: any) => {
            if (hook.section === "body" && typeof hook.cell.raw === "string") {
              hook.cell.text = truncate(hook.cell.raw, 55);
            }
          },

          didDrawPage: (data: any) => {
            const { width, height } = doc.internal.pageSize;

            if (this.logoBase64) {
              if (this.periodoEleitoral) {
                doc.addImage(this.logoBase64, "PNG", width - 125, 12, 35, 35);
              }
              else {
                doc.addImage(this.logoBase64, "PNG", width - 125, 12, 115, 35);
              }
            }

            doc.setFontSize(16)
              .setFont("helvetica", "bold")
              .setTextColor(0, 51, 102);
            doc.text(this.tableTitle, width / 2, 30, { align: "center" });

            const pageStr = `Página ${doc.internal.getNumberOfPages()}`;
            doc.setFontSize(9).setTextColor(40);
            doc.text(pageStr, width - data.settings.margin.right, height - 8, { align: "right" });

            let user = `Usuário logado: ${this.userLogado?.nome}`;
            doc.setFontSize(9).setTextColor(40);
            doc.text(user, 10, height - 8, { align: "left" });
          },
        };
      }
    });

    this.spinner.hide();
  }


  public ImprimirTabela() {
    this.spinner.show();

    for (const col of this.colunasNaoExportaveis) {
      this.table.deleteColumn(col);
    }

    this.table.print();

    this.spinner.hide();
  }

  public ExportarHTML() {
    this.spinner.show();

    for (const col of this.colunasNaoExportaveis) {
      this.table.deleteColumn(col);
    }

    this.table.download("html", `${this.tableTitle}.html`, { style: true });

    this.spinner.hide();
  }

  private buildTable() {
    // limpa o holder caso precise
    this.tableHolder.nativeElement.innerHTML = '';
    this.table = new Tabulator(this.tableHolder.nativeElement, this.options!);
    this.table.on("tableBuilt", () => {

      this.table.setData(this.tableData);

      setTimeout(() => {
        const maximoDePaginas = Number(this.table.getPageMax());
        if (this.userLogado?.currentPage && this.userLogado.currentPage <= maximoDePaginas) {
          this.programmaticPageChange = true;
          this.table.setPage(this.userLogado?.currentPage);
        }
      }, 100);

    },);

    this.table.on("pageLoaded", (pageNumber: number) => {
      if (this.userLogado !== null) {
        if (this.userLogado) {
          if (!this.programmaticPageChange) {
            this.userLogado.currentPage = pageNumber;
            this.authService.setUser(this.userLogado);
          }
        }
      }

      this.programmaticPageChange = false;
    });
    // seus event listeners…
  }




  //Helpers para exportar excel tipado

  private sanitizeSheetName(name: string): string {
    const cleaned = (name || 'Planilha').replace(/[\[\]\:\*\?\/\\]/g, ' ').trim();
    return cleaned.length > 31 ? cleaned.slice(0, 31) : cleaned || 'Planilha';
  }

  private looksLikeBigIdString(v: any): boolean {
    // Ex.: CPF, CNPJ, IDs longos — não converter para número
    return typeof v === 'string' && /^\d+$/.test(v) && v.length >= 11;
  }

  // ===== Resolve valor bruto p/ export (respeita exportValue / accessorDownload / field) =====
  private buildColumnGetters() {
    // pega colunas visíveis e exportáveis
    const colComps = this.table.getColumns()
      .filter(c => (c.isVisible() || (!c.isVisible() && (c.getDefinition() as any)?.download == true)) && (c.getDefinition() as any)?.download !== false);

    return colComps.map(c => {
      const def: any = c.getDefinition();
      const field = c.getField();
      const title = (def?.title ?? field ?? '').toString();

      // Metadados opcionais por coluna (dinâmicos!)
      const exportType: ExportType = (def?.exportType ?? 'auto') as ExportType;
      const exportFormat: string | undefined = def?.exportFormat;
      const exportValueFn: ((row: any, cellValue?: any) => any) | undefined = def?.exportValue || def?.accessorDownload;

      // Getter padrão: exportValue > accessorDownload > field
      const getValue = (row: any) => {
        if (typeof exportValueFn === 'function') {
          // se a página informou função, ela manda!
          return exportValueFn(row, field ? row[field] : undefined);
        }
        return field ? row[field] : undefined;
      };

      return { title, field, exportType, exportFormat, getValue };
    });
  }

  //Metodo para exportar o excel
  public async ExportComoExcel() {
    this.spinner.show();
    try {
      // === mantém SEU fluxo de limpeza ===
      for (const col of this.colunasNaoExportaveis) this.table.deleteColumn(col);
      if (this.tipoRelatorio === 1) this.table.deleteColumn("codigoAtividadePsicologo");
      if (this.tipoRelatorio === 2) this.table.deleteColumn("codigoEnvolvido");

      // === constrói dinamicamente os "getters" por coluna ===
      const cols = this.buildColumnGetters();

      if (cols.length === 0) {
        // nada para exportar
        return;
      }

      const exportFormats = cols
        .map((item, index) => ({ exportFormat: item.exportFormat, exportType: item.exportType, index }))
        .filter(item => item.exportFormat !== undefined);

      // Cabeçalhos + dados crus da tabela
      const headers = cols.map(c => c.title);
      const data = this.table.getData("active");
      const rows = data.map((row: any) => cols.map(c => c.getValue(row)));
      // Cria workbook e sheet
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet(this.sanitizeSheetName(this.tableTitle));

      // Adiciona logo se disponível
      let imageId;
      if (this.logoBase64) {
        // Remove prefixo data:image/png;base64,
        const base64 = this.logoBase64.split(',')[1] || this.logoBase64;
        imageId = workbook.addImage({ base64, extension: 'png' });

        if (this.periodoEleitoral) {
          sheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 70, height: 70 } });
        } else {
          sheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 230, height: 70 } });
        }

        sheet.mergeCells(1, 2, 1, headers.length); // Mescla para título
        sheet.getRow(1).height = 55;
      }

      // Adiciona título
      sheet.getCell(1, 2).value = this.tableTitle;
      sheet.getCell(1, 2).font = { size: 20, bold: true, color: { argb: '003366' } };
      sheet.getCell(1, 2).alignment = { vertical: 'middle', horizontal: 'center' };

      // Linha de espaçamento
      sheet.addRow([]);


      //sheet.getRow(sheet.lastRow.number).font = { bold: true };

      // Adiciona dados
      //for (const row of rows) {
      //  console.log(row)
      //  sheet.addRow(row);
      //}
      const headersFiltrados = [...headers];
      headersFiltrados.splice(-1, 1);
      sheet.addRow(headersFiltrados);

      for (const row of rows) {
        const rowSemSub = [...row];
        rowSemSub.splice(-1, 1);

        // Adiciona a linha principal sem o campo da subtabela
        sheet.addRow(rowSemSub);
        if (this.subTable && Array.isArray(row[row.length - 1]) && row[row.length - 1].length) {

          const colunasSub = this.subColumnNames?.(null, row) ?? [];

          const titulos = colunasSub.map(col => col.title);

          const headerSub = sheet.addRow(titulos);
          headerSub.font = { bold: true };
          headerSub.alignment = { horizontal: 'center' };

          for (const subRow of row[row.length - 1]) {
            const valores = colunasSub.map(col => subRow[col.field] ?? '');
            const ref = sheet.addRow(valores);

            ref.font = { italic: true, color: { argb: 'FF555555' } };
            ref.alignment = { indent: 1 };


            // Linha de espaçamento
            sheet.addRow([]);

            //  sheet.addRow(headersFiltrados);
          }
        }

      }

      if (exportFormats && exportFormats.length > 0 && sheet.lastRow) {
        for (let i = 4; i <= sheet.lastRow.number; i++) {
          const row = sheet.getRow(i);

          for (let j = 0; j < exportFormats.length; j++) {
            const cell = row.getCell(exportFormats[j].index + 1);
            const value = cell.value as string;

            if (exportFormats[j] && exportFormats[j].exportType == "time"
              && typeof value === "string" && /^\d{1,2}:\d{2}$/.test(value)) {
              const [h, m] = value.split(":").map(Number);
              cell.value = (h * 60 + m) / (24 * 60);

              if (exportFormats[j]?.exportFormat)
                cell.numFmt = exportFormats[j]?.exportFormat ?? "";
            }
          }
        }
      }

      sheet.addRow([]);

      if (sheet.lastRow) {
        sheet.mergeCells(sheet.lastRow.number + 1, 1, sheet.lastRow.number + 1, headers.length);
        sheet.getCell(sheet.lastRow.number, 1).value = `Usuário logado: ${this.userLogado?.nome}`;
      }

      // Ajusta largura das colunas
      headers.forEach((h, i) => {
        sheet.getColumn(i + 1).width = Math.max(12, String(h).length + 4);
      });

      // Gera arquivo
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${this.tableTitle}.xlsx`);
    } finally {
      this.spinner.hide();
    }
  }

  private loadLogo() {
    const logoUrl = this.periodoEleitoral
      ? 'https://sedseecdn.azureedge.net/images/favicon_eleitoral/ms-icon-70x70.png'
      : 'assets/img/logo-gov-sp.png';

    this.http.get(logoUrl, { responseType: 'blob' })
      .subscribe(blob => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.logoBase64 = reader.result as string;
        };
        reader.readAsDataURL(blob);
      });
  }
}
